import { useNuxtData } from '#imports'
import { computed } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import type { GetListingsFiltersMetadataResponse } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { OPTION_VALUE_ALL } from '../components/Filters/Filters.constants'
import translations from '../components/Filters/Filters.translations'

export function useFiltersOptions() {
  const i18n = useI18n()
  const { activeMarkets } = useSellerInfoStore()

  const { data: metadata } = useHttpFetch(
    merchantsAPI.getListingsFiltersMetadata,
    {
      dedupe: 'cancel',
      getCachedData: (key) =>
        useNuxtData<GetListingsFiltersMetadataResponse>(key).data.value ||
        undefined,
    },
  )

  const genericOptionAll = {
    label: i18n(translations.all),
    value: OPTION_VALUE_ALL,
  }

  const allOptions = {
    alert: i18n(translations.alertsAll),
    backbox: i18n(translations.backboxAll),
    grade: i18n(translations.gradesAll),
    market: i18n(translations.marketsAll),
    stock: i18n(translations.withAndWithoutStock),
    categories: i18n(translations.categoryAll),
    hasNewBattery: i18n(translations.hasNewBatteryAll),
  }

  const hasNewBatteryOptions = [
    { label: i18n(translations.hasNewBatteryAll), value: OPTION_VALUE_ALL },
    { label: i18n(translations.hasNewBatteryNew), value: 'true' },
    { label: i18n(translations.hasNewBatteryRefurbished), value: 'false' },
  ]

  const backboxOptions = [
    { label: i18n(translations.backboxAll), value: OPTION_VALUE_ALL },
    { label: i18n(translations.withBackbox), value: 'true' },
    { label: i18n(translations.withoutBackbox), value: 'false' },
  ]

  const stockOptions = [
    { label: i18n(translations.withAndWithoutStock), value: OPTION_VALUE_ALL },
    { label: i18n(translations.withStock), value: 'true' },
    { label: i18n(translations.withoutStock), value: 'false' },
  ]

  const alertOptions = [
    { label: i18n(translations.alertsAll), value: OPTION_VALUE_ALL },
    { label: i18n(translations.priceAboveNew), value: 'true' },
  ]

  const availableMarketsOptions = [
    genericOptionAll,
    ...activeMarkets.map((market) => ({
      label: i18n.country(market),
      value: market,
    })),
  ]

  const gradesOptions = computed(() => [
    { label: i18n(translations.gradesAll), value: OPTION_VALUE_ALL },
    ...(metadata.value?.authorizedGrades || []).map((grade) => ({
      label: grade.label,
      value: grade.code,
    })),
  ])

  return {
    alertOptions,
    allOptions,
    availableMarketsOptions,
    backboxOptions,
    gradesOptions,
    hasNewBatteryOptions,
    stockOptions,
  }
}
